import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { AvatarSizes, ListItem, ListItemProps, Skeleton, Stack } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface AnonymousListItemProps extends ListItemProps {
  uuid?: string | null;
  index?: number;
  employee: ApiEmployee | null | undefined;
  isLoading?: boolean;
}

const skeletonSizeMap: Record<keyof AvatarSizes, string> = {
  xs: "w-4 h-4 rounded-full",
  sm: "w-5 h-5 rounded-xs",
  md: "w-9 h-9 rounded-sm",
  lg: "w-12 h-12 rounded-md",
  xl: "w-16 h-16 rounded-lg",
  "2xl": "w-41 h-41 rounded-lg",
  "4xl": "w-57 h-57 rounded-lg",
};

export const AnonymousListItem = ({
  uuid,
  index,
  employee,
  isLoading,
  size,
  ...props
}: AnonymousListItemProps) => {
  const { t } = useTranslation();
  const anonymousVariants = [
    { emoji: emojiDefaults.anonymous.panda, text: t("core:anonymous_panda") },
    { emoji: emojiDefaults.anonymous.squirrel, text: t("core:anonymous_squirrel") },
    { emoji: emojiDefaults.anonymous.koala, text: t("core:anonymous_koala") },
    { emoji: emojiDefaults.anonymous.penguin, text: t("core:anonymous_penguin") },
    { emoji: emojiDefaults.anonymous.bug, text: t("core:anonymous_bug") },
    { emoji: emojiDefaults.anonymous.cow, text: t("core:anonymous_cow") },
    { emoji: emojiDefaults.anonymous.shrimp, text: t("core:anonymous_shrimp") },
    { emoji: emojiDefaults.anonymous.dragon, text: t("core:anonymous_dragon") },
    { emoji: emojiDefaults.anonymous.dog, text: t("core:anonymous_dog") },
  ];

  const personKey = uuid ? parseInt(uuid.slice(0, 8), 16) : (index ?? 0);

  const variant = useMemo(() => {
    if (personKey !== undefined) {
      const idx = Math.abs(personKey) % anonymousVariants.length;
      return anonymousVariants[idx];
    }

    // return anonymousVariants[Math.floor(Math.random() * (anonymousVariants.length - 1))];

    return { emoji: emojiDefaults.default, text: t("common:anonymous") };
  }, [personKey]);

  if (isLoading) {
    return (
      <Stack direction={"horizontal"} gap={"md"} items={"center"}>
        <Skeleton className={skeletonSizeMap[size ?? "md"]} />
        <Stack gap={"xs"}>
          <Skeleton className={"h-4 w-32"} />
          <Skeleton className={"h-3 w-24"} />
        </Stack>
      </Stack>
    );
  }

  if (employee) {
    return <EmployeeListItem employee={employee} size={size} {...props} />;
  }

  return (
    <>
      <ListItem
        title={variant.text}
        emoji={variant.emoji}
        forceShowAvatar={true}
        size={size}
        {...props}
      />
    </>
  );
};
